import type { UserRole } from "@/types";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  UserCheck,
  BarChart3,
  Settings,
  Briefcase,
  Building2,
  MessageCircle,
  ListTodo,
  Phone,
} from "lucide-react";

type Permission = {
  module: string;
  actions: string[];
};

type NavigationShell = "admin" | "operational";

export interface NavigationItem {
  label: string;
  path: string;
  icon: React.ElementType;
  module: string;
  children?: NavigationItem[];
}

interface NavigationConfigItem {
  label: string;
  icon: React.ElementType;
  module: string;
  shell: NavigationShell;
  roles: UserRole[];
  pathByRole: Partial<Record<UserRole, string>>;
  requiredActions?: string[];
  children?: NavigationConfigItem[];
}

const NAVIGATION_CONFIG: NavigationConfigItem[] = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    module: "dashboard",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/dashboard" },
    children: [
      {
        label: "Main",
        icon: LayoutDashboard,
        module: "dashboard",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/dashboard" },
      },
      {
        label: "EMI Collections",
        icon: LayoutDashboard,
        module: "dashboard",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/dashboard/emi" },
      },
    ],
  },
  {
    label: "Defaulters",
    icon: Users,
    module: "debtors",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/debtors" },
  },
  {
    label: "Cases",
    icon: Briefcase,
    module: "cases",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/cases" },
  },
  {
    label: "Payments",
    icon: CreditCard,
    module: "payments",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/payments" },
  },
  {
    label: "Agents",
    icon: UserCheck,
    module: "agents",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/agents" },
  },
  {
    label: "HRM",
    icon: Building2,
    module: "hrm",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/hrm/salary-incentives" },
    children: [
      {
        label: "Salary & Incentives",
        icon: Building2,
        module: "salary_incentives",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/hrm/salary-incentives" },
      },
      {
        label: "Incentive Configurations",
        icon: Building2,
        module: "incentive_configurations",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/hrm/incentive-configurations" },
      },
    ],
  },
  {
    label: "Reports",
    icon: BarChart3,
    module: "reports",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/reports" },
  },
  {
    label: "Notifications",
    icon: MessageCircle,
    module: "reports",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/admin/notifications" },
  },
  {
    label: "Settings",
    icon: Settings,
    module: "settings",
    shell: "admin",
    roles: ["admin"],
    pathByRole: { admin: "/settings" },
    children: [
      {
        label: "Regions",
        icon: Settings,
        module: "regions",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/settings/regions" },
      },
      {
        label: "Banks",
        icon: Settings,
        module: "banks",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/settings/banks" },
      },
      {
        label: "Targets",
        icon: Settings,
        module: "targets",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/settings/targets" },
      },
      {
        label: "Permissions",
        icon: Settings,
        module: "permissions",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/settings/permissions" },
      },
      {
        label: "Levels & Designations",
        icon: Settings,
        module: "settings",
        shell: "admin",
        roles: ["admin"],
        pathByRole: { admin: "/settings/levels-designations" },
      },
    ],
  },
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    module: "dashboard",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/dashboard",
      team_leader: "/dashboard",
    },
  },
  {
    label: "Defaulters",
    icon: Users,
    module: "debtors",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/debtors",
      team_leader: "/debtors",
    },
  },
  {
    label: "Cases",
    icon: Briefcase,
    module: "cases",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/cases",
      team_leader: "/cases",
    },
  },
  {
    label: "Payments",
    icon: CreditCard,
    module: "payments",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/payments",
      team_leader: "/payments",
    },
  },
  {
    label: "Agents",
    icon: UserCheck,
    module: "agents",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/agents",
      team_leader: "/agents",
    },
  },
  {
    label: "Reports",
    icon: BarChart3,
    module: "reports",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/reports",
      team_leader: "/reports",
    },
  },
  {
    label: "Notifications",
    icon: MessageCircle,
    module: "reports",
    shell: "operational",
    roles: ["manager", "team_leader"],
    pathByRole: {
      manager: "/admin/notifications",
      team_leader: "/admin/notifications",
    },
  },
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    module: "agent_portal",
    shell: "operational",
    roles: ["agent", "telecaller"],
    pathByRole: { agent: "/agent-portal" },
  },
  // {
  //   label: "Dashboard",
  //   icon: LayoutDashboard,
  //   module: "dashboard",
  //   shell: "operational",
  //   roles: ["telecaller"],
  //   pathByRole: { telecaller: "/agent-portal" },
  // },
  {
    label: "My Cases",
    icon: Briefcase,
    module: "cases",
    shell: "operational",
    roles: ["agent", "telecaller"],
    pathByRole: {
      agent: "/agent-portal/cases",
      telecaller: "/agent-portal/cases",
    },
  },
  {
    label: "Daily Tasks",
    icon: ListTodo,
    module: "cases",
    shell: "operational",
    roles: ["agent", "telecaller"],
    pathByRole: {
      agent: "/agent-portal/tasks",
      telecaller: "/agent-portal/tasks",
    },
  },
  {
    label: "Call Logs",
    icon: Phone,
    module: "calls",
    shell: "operational",
    roles: ["agent", "telecaller"],
    pathByRole: { agent: "/agent-portal/calls" },
    requiredActions: ["view", "cancall"],
  },
  {
    label: "Defaulters",
    icon: Users,
    module: "debtors",
    shell: "operational",
    roles: ["agent", "manager", "team_leader"],
    pathByRole: {
      agent: "/debtors",
      manager: "/debtors",
      team_leader: "/debtors",
    },
    requiredActions: ["view"],
  },
  {
    label: "My Performance",
    icon: BarChart3,
    module: "reports",
    shell: "operational",
    roles: ["agent"],
    pathByRole: { agent: "/agent-portal/performance" },
  },
  {
    label: "Notifications",
    icon: MessageCircle,
    module: "reports",
    shell: "operational",
    roles: ["agent"],
    pathByRole: { agent: "/agent-portal/notifications" },
  },
];

const canViewModule = (permissions: Permission[], module: string) =>
  permissions.some(
    (permission) =>
      permission.module === module && permission.actions.includes("view"),
  );

const hasRequiredAction = (
  permissions: Permission[],
  module: string,
  requiredActions?: string[],
) =>
  !requiredActions?.length ||
  permissions.some(
    (permission) =>
      permission.module === module &&
      requiredActions.some((action) => permission.actions.includes(action)),
  );

const canAccessItem = (
  permissions: Permission[],
  item: NavigationConfigItem,
) =>
  item.requiredActions?.length
    ? hasRequiredAction(permissions, item.module, item.requiredActions)
    : canViewModule(permissions, item.module);

const resolveItem = (
  item: NavigationConfigItem,
  role: UserRole,
  permissions: Permission[],
): NavigationItem | null => {
  if (!item.roles.includes(role)) {
    return null;
  }

  const path = item.pathByRole[role];
  if (!path) {
    return null;
  }

  const children = item.children
    ?.map((child) => resolveItem(child, role, permissions))
    .filter(Boolean) as NavigationItem[] | undefined;

  if (
    !canAccessItem(permissions, item) &&
    (!children || children.length === 0)
  ) {
    return null;
  }

  return {
    label: item.label,
    path,
    icon: item.icon,
    module: item.module,
    children: children?.length ? children : undefined,
  };
};

export function getNavigationItems(
  role: UserRole,
  permissions: Permission[],
  shell: NavigationShell,
): NavigationItem[] {
  return NAVIGATION_CONFIG.filter((item) => item.shell === shell)
    .map((item) => resolveItem(item, role, permissions))
    .filter(Boolean) as NavigationItem[];
}
